import type { LobbyServerEvent } from '@danda/shared';
import type { ChatMessage } from '../lobbyStore.types';
import type { EventHandlerContext } from './lobby-event-types';
import { resolveCharacterAvatarUrl, resolveCharacterAvatarEmoji } from './resolve-avatar';
import { addOrEnqueueMessage, msgId } from './stream-utils';

import type { LobbyState } from '../lobbyStore.types';

/** Shared logic for incoming DM messages (dm-message and dm-received) */
function handleIncomingDm(
  state: LobbyState,
  fromCharId: string,
  fromCharName: string,
  message: string,
  timestamp: string,
  msgType: ChatMessage['type']
) {
  const roomId = `dm-${fromCharId}`;
  const fromChar = state.characters.find((c) => c.id === fromCharId);
  const lobbyChar = state.lobbyCharacters.find((c) => c.id === fromCharId);
  const avatarUrl = resolveCharacterAvatarUrl(lobbyChar, fromChar);
  const avatarEmoji = resolveCharacterAvatarEmoji(lobbyChar, fromChar);

  const existingRoom = state.chatRooms.find((r) => r.id === roomId);
  const participantRoom = state.chatRooms.find(
    (r) => r.type === 'private' && r.participants.includes(fromCharId)
  );
  const room = existingRoom ?? participantRoom;
  const targetRoomId = room?.id ?? roomId;

  if (!room) {
    state.addChatRoom({
      id: targetRoomId,
      name: fromCharName,
      participants: [fromCharId],
      messages: [],
      unreadCount: 0,
      type: 'private',
      avatar: avatarUrl,
      avatarUrl,
      avatarEmoji,
    });
  } else if (!room.avatar && !room.avatarUrl) {
    state.updateChatRoom(targetRoomId, { avatar: avatarUrl, avatarUrl, avatarEmoji });
  }

  state.addMessage(targetRoomId, {
    id: msgId('dm'),
    senderId: fromCharId,
    senderName: fromCharName,
    content: message,
    timestamp: new Date(timestamp),
    type: msgType,
    avatar: avatarUrl,
    avatarUrl,
    avatarEmoji,
  });
}

export function handleDmEvents(event: LobbyServerEvent, ctx: EventHandlerContext): boolean {
  const { get, set } = ctx;
  const state = get();

  switch (event.type) {
    case 'lobby:dm-message': {
      handleIncomingDm(
        state,
        event.fromCharacterId,
        event.fromCharacterName,
        event.message,
        event.sent_at,
        event.isNpc ? 'npc' : 'user'
      );
      return true;
    }

    case 'lobby:dm-received': {
      handleIncomingDm(
        state,
        event.fromCharacterId,
        event.fromCharacterName,
        event.message,
        event.sent_at,
        'user'
      );
      return true;
    }

    case 'lobby:dm-sent': {
      const dmSentRoomId = `dm-${event.toCharacterId}`;
      const sentRoom = state.chatRooms.find((r) => r.id === dmSentRoomId);
      const participantRoom = state.chatRooms.find(
        (r) => r.type === 'private' && r.participants.includes(event.toCharacterId)
      );
      const room = sentRoom ?? participantRoom;
      const targetRoomId = room?.id ?? dmSentRoomId;

      const toChar = state.characters.find((c) => c.id === event.toCharacterId);
      const lobbyChar = state.lobbyCharacters.find((c) => c.id === event.toCharacterId);
      const avatarUrl = resolveCharacterAvatarUrl(lobbyChar, toChar);
      const avatarEmoji = resolveCharacterAvatarEmoji(lobbyChar, toChar);
      if (!room) {
        state.addChatRoom({
          id: targetRoomId,
          name: event.toCharacterName,
          participants: [event.toCharacterId],
          messages: [],
          unreadCount: 0,
          type: 'private',
          avatar: avatarUrl,
          avatarUrl,
          avatarEmoji,
        });
      } else if (!room.avatar && !room.avatarUrl) {
        state.updateChatRoom(targetRoomId, { avatar: avatarUrl, avatarUrl, avatarEmoji });
      }
      const myCharId = state.selectedCharacterId;
      const myChar = myCharId ? state.characters.find((c) => c.id === myCharId) : undefined;
      state.addMessage(targetRoomId, {
        id: msgId('dm-sent'),
        senderId: myCharId ?? 'user',
        senderName: myChar?.name ?? 'You',
        content: event.message,
        timestamp: new Date(event.sent_at),
        type: 'user',
      });
      return true;
    }

    // Tavian requests opening a 1:1 chat panel with a character
    case 'lobby:start-char-chat': {
      const startChatRoomId = `char-chat-${event.characterId}`;
      // Check if room already exists for this character
      const existingRoom = state.chatRooms.find(
        (r) => r.type === 'private' && r.participants.includes(event.characterId)
      );
      if (existingRoom) {
        state.switchChatRoom(existingRoom.id);
        return true;
      }

      const targetChar = state.characters.find((c) => c.id === event.characterId);
      const lobbyChar = state.lobbyCharacters.find((c) => c.id === event.characterId);
      const avatarUrl = resolveCharacterAvatarUrl(lobbyChar, targetChar);
      const avatarEmoji = resolveCharacterAvatarEmoji(lobbyChar, targetChar);

      state.addChatRoom({
        id: startChatRoomId,
        name: event.characterName,
        participants: [event.characterId],
        messages: [],
        unreadCount: 0,
        type: 'private',
        avatar: avatarUrl,
        avatarUrl,
        avatarEmoji,
      });
      state.switchChatRoom(startChatRoomId);
      return true;
    }

    case 'lobby:char-chat-started': {
      const charChatRoomId = `char-chat-${event.chatSessionId}`;

      // Handle both Rust backend format (participants array) and legacy format
      let targetCharId: string | undefined;
      let targetCharName: string | undefined;
      let partnerUserId: string | undefined;
      let participantIds: string[] = [];

      if (event.participants && event.participants.length > 0) {
        // Rust backend format: participants array
        participantIds = event.participants.map((p) => p.charId);
        // Find the "other" participant (not the current user's character)
        const otherParticipant = event.participants.find(
          (p) => p.charId !== state.selectedCharacterId
        );
        if (otherParticipant) {
          targetCharId = otherParticipant.charId;
          targetCharName = otherParticipant.charName;
          partnerUserId = otherParticipant.userId;
        }
      } else {
        // Legacy TypeScript format
        targetCharId = event.targetCharacterId;
        targetCharName = event.targetCharacterName;
        if (event.initiatorCharacterId && event.targetCharacterId) {
          participantIds = [event.initiatorCharacterId, event.targetCharacterId];
        }
      }

      if (!targetCharId) {
        console.warn('[LobbyStore] char-chat-started missing target character');
        return true;
      }

      // Check for existing room with same target character to prevent duplicates
      const existingRoomForChar = state.chatRooms.find(
        (r) => r.id.startsWith('char-chat-') && r.participants.includes(targetCharId!)
      );
      if (existingRoomForChar) {
        // Migrate room ID to chatSessionId for consistent message/close routing
        // (start-char-chat creates room with characterId, but messages use chatSessionId)
        if (existingRoomForChar.id !== charChatRoomId) {
          set((s) => ({
            chatRooms: s.chatRooms.map((r) =>
              r.id === existingRoomForChar.id
                ? {
                    ...r,
                    id: charChatRoomId,
                    participants: participantIds.length > 0 ? participantIds : r.participants,
                  }
                : r
            ),
          }));
        }
        state.switchChatRoom(charChatRoomId);
        return true;
      }

      const targetChar = state.characters.find((c) => c.id === targetCharId);
      const lobbyChar = state.lobbyCharacters.find((c) => c.id === targetCharId);
      const avatarUrl = resolveCharacterAvatarUrl(lobbyChar, targetChar);
      const avatarEmoji = resolveCharacterAvatarEmoji(lobbyChar, targetChar);

      state.addChatRoom({
        id: charChatRoomId,
        name: targetCharName || 'Chat',
        participants: participantIds,
        messages: [],
        unreadCount: 0,
        type: 'private',
        avatar: avatarUrl,
        avatarUrl,
        avatarEmoji,
        partnerUserId,
      });
      state.switchChatRoom(charChatRoomId);
      return true;
    }

    // New participant joined a character chat session (N:N chat support)
    case 'lobby:char-chat-participant-joined': {
      const joinedRoomId = `char-chat-${event.chatSessionId}`;
      const room = state.chatRooms.find((r) => r.id === joinedRoomId);
      if (room) {
        const newParticipantId = event.participant.charId;
        if (!room.participants.includes(newParticipantId)) {
          state.updateChatRoom(joinedRoomId, {
            participants: [...room.participants, newParticipantId],
          });
        }
        // Add system message about new participant
        state.addMessage(joinedRoomId, {
          id: `sys-${Date.now()}`,
          senderId: 'system',
          senderName: 'System',
          content: `${event.participant.charName} joined the chat`,
          timestamp: new Date(event.sent_at),
          type: 'system',
        });
      }
      return true;
    }

    case 'lobby:char-chat-closed': {
      const closedRoomId = `char-chat-${event.chatSessionId}`;
      state.removeChatRoom(closedRoomId);
      if (state.activeChatRoomId === closedRoomId) {
        state.switchChatRoom('main');
      }
      return true;
    }

    case 'lobby:char-chat-invite': {
      // Support both TypeScript and Rust backend field names
      const chatSessionId = event.chatSessionId ?? event.chatId;
      const legacyEvent = event as typeof event & {
        inviterCharacterName?: string;
        inviterCharacterId?: string;
      };
      const inviterName = legacyEvent.inviterCharacterName ?? event.characterName ?? 'Someone';
      const inviterId = legacyEvent.inviterCharacterId ?? event.characterId;

      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Chat Invite',
        message: `${inviterName} wants to chat with you`,
        timestamp: new Date(event.sent_at ?? Date.now()),
        isRead: false,
        data: { chatSessionId, inviterCharacterId: inviterId },
      });
      return true;
    }

    case 'lobby:char-chat-message': {
      const charMsgRoomId = `char-chat-${event.chatSessionId}`;
      set({ isWaitingForResponse: false });

      const fromChar = state.characters.find((c) => c.id === event.fromCharacterId);
      const lobbyChar = state.lobbyCharacters.find((c) => c.id === event.fromCharacterId);
      const avatarUrl = resolveCharacterAvatarUrl(lobbyChar, fromChar);
      const avatarEmoji = resolveCharacterAvatarEmoji(lobbyChar, fromChar);

      const room = state.chatRooms.find((r) => r.id === charMsgRoomId);
      if (!room) return true;

      // Backfill room avatar if missing (older rooms created before avatar support)
      if (!room.avatar && !room.avatarUrl) {
        state.updateChatRoom(charMsgRoomId, { avatar: avatarUrl, avatarUrl, avatarEmoji });
      }

      // Real lobby NPC roster ids look like `npc_2f29f7e7` (see
      // `data/characters/lobby_npcs/*.json`). The `char_npc*` prefix only
      // appears in test fixtures, so trust the explicit `isNpc` field the
      // Rust backend sends and fall back to the `npc_` prefix for older
      // payloads. Mirrors `npc-events.ts::HUMAN_TYPING_NPCS` intent.
      const isNpcSender = event.isNpc === true || event.fromCharacterId.startsWith('npc_');

      // The Rust `CharChatMessage` (backend/shared/src/types/lobby_websocket.rs)
      // has no `is_streaming` field — char-chat NPC responses arrive as a
      // single non-streaming `CharChatMessage { is_npc: true }`. The
      // streaming + mid-stream-finalization branches that were here previously
      // were unreachable dead code per cycle-3 review. Route the single
      // message straight through `addOrEnqueueMessage` so canned typewriter
      // picks it up.
      const newMessage: ChatMessage = {
        id: msgId('char-msg'),
        senderId: event.fromCharacterId,
        senderName: event.fromCharacterName,
        content: event.message,
        timestamp: new Date(event.sent_at),
        type: isNpcSender ? 'npc' : 'user',
        avatar: avatarUrl,
        avatarUrl,
        avatarEmoji,
      };

      if (isNpcSender) {
        addOrEnqueueMessage(state, charMsgRoomId, newMessage);
      } else {
        state.addMessage(charMsgRoomId, newMessage);
      }
      return true;
    }

    default:
      return false;
  }
}
